/* S2–S11 read/decision surfaces. Each renders Stamped data into Pop-Bento cards
 * and stamps every tile. Interactive writes (action decide, update ack) go back
 * through the StoreClient — the same seam the engine will implement. */
import { getStore, BridgeError } from '../store';
import { esc, stampLine, surfaceTitle, card, loading, note, offlineBanner } from '../components/ui';

/* ---- S2 · TODAY ---- */
export async function renderToday(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Today') + loading();
  const s = await getStore().getToday();
  const tiles = s.data.tiles
    .map((t) =>
      card(
        `<div class="label">${esc(t.title)}</div>${
          t.value ? `<div class="big">${esc(t.value)}</div>` : ''
        }${t.sub ? `<p>${esc(t.sub)}</p>` : ''}`,
        t.fill ?? '',
      ),
    )
    .join('');
  root.innerHTML =
    surfaceTitle('Today') +
    card(`<h3>${esc(s.data.greeting)}</h3>${stampLine(s)}`, 'wide') +
    `<div class="bento">${tiles}</div>`;
}

/* ---- S3 · ACTIONS ---- */
export async function renderActions(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Actions') + loading();
  const store = getStore();
  const s = await store.getActions();
  const banner = s.freshness === 'offline' ? offlineBanner(s.source) : '';
  const draw = (html: string) => (root.innerHTML = surfaceTitle('Actions') + banner + html + stampLine(s));
  const cards = () =>
    s.data
      .map(
        (a) => `
      <div class="card wide" data-id="${esc(a.id)}">
        <div class="label">${esc(a.kind)} · ${esc(a.channel)}</div>
        <p><strong>Context:</strong> ${esc(a.context)}</p>
        <p><strong>Proposal:</strong> ${esc(a.proposal)}</p>
        <p><strong>${esc(a.explain)}</strong></p>
        ${
          a.state === 'pending'
            ? `<div style="display:flex;gap:8px">
                 <button class="btn" data-act="approve" data-id="${esc(a.id)}">Approve</button>
                 <button class="btn ghost" data-act="skip" data-id="${esc(a.id)}">Skip</button>
               </div>`
            : `<span class="pill"><span class="dot ${a.state === 'skipped' ? 'attention' : 'good'}"></span>${esc(
                a.state,
              )}${a.receipt ? ' · ' + esc(a.receipt) : ''}</span>`
        }
      </div>`,
      )
      .join('');
  draw(cards());
  root.onclick = async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn) return;
    try {
      await store.decideAction(btn.dataset.id!, btn.dataset.act as 'approve' | 'skip');
      renderActions(root);
    } catch (err) {
      // Not-wired (501) or engine error → honest note on the card, never a fake "sent".
      const card = btn.closest<HTMLElement>('.card');
      const msg =
        err instanceof BridgeError && err.notWired
          ? "Actions aren't wired to a live queue yet — nothing was sent."
          : `Couldn't complete that: ${esc(String((err as Error)?.message ?? err))}`;
      if (card) card.insertAdjacentHTML('beforeend', note(msg));
    }
  };
}

/* ---- S4 · UPDATES ---- */
export async function renderUpdates(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Updates') + loading();
  const store = getStore();
  const s = await store.getUpdates();
  root.innerHTML =
    surfaceTitle('Updates') +
    (s.freshness === 'offline' ? offlineBanner(s.source) : '') +
    s.data
      .map((u) => {
        const dot = u.severity === 'urgent' ? 'attention' : u.severity === 'needs-nick' ? 'watch' : 'good';
        return card(
          `<span class="pill"><span class="dot ${dot}"></span>${esc(u.severity)} · ${esc(u.domain)}</span>
           <h3>${esc(u.headline)}</h3>
           <ul style="margin:4px 0 0;padding-left:18px">${u.bullets.map((b) => `<li>${esc(b)}</li>`).join('')}</ul>
           ${u.acked ? '<span class="pill">acked</span>' : `<button class="btn ghost" data-ack="${esc(u.id)}">Ack</button>`}`,
          'wide',
        );
      })
      .join('') +
    stampLine(s);
  root.onclick = async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-ack]');
    if (!b) return;
    try {
      await store.ackUpdate(b.dataset.ack!);
      renderUpdates(root);
    } catch (err) {
      const msg =
        err instanceof BridgeError && err.notWired
          ? "Ack isn't wired to the live feed yet."
          : `Couldn't ack: ${esc(String((err as Error)?.message ?? err))}`;
      b.insertAdjacentHTML('afterend', note(msg));
    }
  };
}

/* ---- S5 · HEALTH ---- */
export async function renderHealth(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Health') + loading();
  const s = await getStore().getHealth();
  const panels = s.data.panels
    .map(
      (p) => `
    <div class="card wide">
      <h3>${esc(p.person)} <span class="label">${esc(p.scope)}</span></h3>
      ${p.markers
        .map(
          (m) =>
            `<div class="pill"><span class="dot ${m.status}"></span>${esc(m.name)}: ${esc(
              m.projectedNow,
            )} · ${esc(m.confidence)}</div>`,
        )
        .join('')}
      <p class="label">Reps due</p>
      <ul style="margin:0;padding-left:18px">${p.repsDue.map((r) => `<li>${esc(r)}</li>`).join('')}</ul>
    </div>`,
    )
    .join('');
  root.innerHTML = surfaceTitle('Health') + panels + stampLine(s);
}

/* ---- S6 · FINANCES ---- */
export async function renderFinances(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Finances') + loading();
  const s = await getStore().getFinances();
  const lines = s.data.budgetLines
    .map((l) => {
      const over = l.actual > l.target;
      return `<div class="pill"><span class="dot ${over ? 'attention' : 'good'}"></span>${esc(
        l.name,
      )}: ${l.actual} / ${l.target}</div>`;
    })
    .join('');
  root.innerHTML =
    surfaceTitle('Finances') +
    card(`<div class="label">Net pulse</div><div class="big">${esc(s.data.netPulse)}</div>`, 'wide blush') +
    card(lines, 'wide') +
    stampLine(s);
}

/* ---- S7 · BUSINESS ---- */
export async function renderBusiness(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Business') + loading();
  const s = await getStore().getBusiness();
  const kpis = s.data.kpis
    .map((k) => card(`<div class="label">${esc(k.title)}</div><div class="big">${esc(k.value ?? '—')}</div>`, k.fill ?? ''))
    .join('');
  root.innerHTML =
    surfaceTitle('Business') +
    `<div class="bento">${kpis}</div>` +
    card(`<h3>Open loops</h3><ul style="margin:0;padding-left:18px">${s.data.openLoops.map((o) => `<li>${esc(o)}</li>`).join('')}</ul>`, 'wide') +
    stampLine(s);
}

/* ---- S8 · CALENDAR / TO-DO / SHOPPING (three routes) ---- */
export async function renderCalendar(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Calendar') + loading();
  const s = await getStore().getCalendar();
  root.innerHTML =
    surfaceTitle('Calendar') +
    s.data.map((e) => card(`<div class="label">${esc(e.when)}</div><p>${esc(e.title)}</p>`, 'wide sky')).join('') +
    stampLine(s);
}
export async function renderTodos(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('To-Do') + loading();
  const s = await getStore().getTodos();
  root.innerHTML =
    surfaceTitle('To-Do') +
    s.data
      .map((t) => card(`<div class="pill"><span class="dot ${t.done ? 'good' : 'watch'}"></span>${esc(t.title)}</div>`, 'wide'))
      .join('') +
    stampLine(s);
}
export async function renderShopping(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Shopping') + loading();
  const s = await getStore().getShopping();
  root.innerHTML =
    surfaceTitle('Shopping') +
    s.data
      .map((i) => card(`<div class="pill"><span class="dot ${i.got ? 'good' : 'watch'}"></span>${esc(i.title)}</div>`, 'wide'))
      .join('') +
    stampLine(s);
}

/* ---- S9 · EXTRAS (Mac-arm proxied, fail-soft) ---- */
export async function renderExtras(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Extras') + loading();
  const s = await getStore().getExtras();
  const banner = s.data.online
    ? ''
    : `<div class="softfail">Mac is offline — these features (vault, transcription, WhatsApp) are queued and will run when it's back.</div>`;
  const svcs = s.data.services
    .map((sv) => `<div class="pill"><span class="dot ${sv.ok ? 'good' : 'attention'}"></span>${esc(sv.name)}</div>`)
    .join('');
  root.innerHTML = surfaceTitle('Extras') + banner + card(svcs, 'wide') + stampLine(s);
}

/* ---- S10 · SKIPPY SCHOOL ---- */
export async function renderSchool(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Skippy School') + loading();
  const s = await getStore().getSchool();
  root.innerHTML =
    surfaceTitle('Skippy School') +
    s.data.kids.map((k) => card(`<h3>${esc(k.name)}</h3><p>${esc(k.lastResult)}</p>`, 'wide mint')).join('') +
    stampLine(s);
}

/* ---- S11 · SETTINGS / TRUST ---- */
export async function renderSettings(root: HTMLElement): Promise<void> {
  root.innerHTML = surfaceTitle('Settings & Trust') + loading();
  const s = await getStore().getSettings();
  const ladder = s.data.trustLadder
    .map(
      (c) =>
        `<div class="pill"><span class="dot ${c.autoSend ? 'watch' : 'good'}"></span>${esc(c.label)} — ${
          c.autoSend ? 'auto-send' : 'per-tap'
        } (${c.approvedUnchanged}×)</div>`,
    )
    .join('');
  // The bridge the app points at (runtime override wins, else the build-time env).
  // Editing it here lets Nick repoint the app at a bridge WITHOUT a rebuild — the
  // store factory reads localStorage.skippyBridge first (store/index.ts).
  let currentBridge = '';
  try {
    currentBridge = (typeof localStorage !== 'undefined' && localStorage.getItem('skippyBridge')) || '';
  } catch {
    /* localStorage can throw (private mode) — treat as unset */
  }
  if (!currentBridge) currentBridge = (import.meta.env.VITE_SKIPPY_BRIDGE as string | undefined) ?? '';

  root.innerHTML =
    surfaceTitle('Settings & Trust') +
    card(
      `<div class="label">API spend today</div><div class="big">$${s.data.spendTodayUSD.toFixed(2)}</div><p>cap $${s.data.spendCapUSD.toFixed(
        2,
      )}</p>`,
      'wide butter',
    ) +
    card(`<h3>Trust ladder</h3>${ladder}`, 'wide') +
    card(
      `<div class="pill"><span class="dot ${s.data.macArmOnline ? 'good' : 'attention'}"></span>Mac arm ${
        s.data.macArmOnline ? 'online' : 'offline'
      }</div>`,
      'wide',
    ) +
    card(
      `<h3>Bridge URL</h3>
       <p>Point the app at an engine/store bridge without rebuilding.</p>
       <input id="bridgeUrl" type="url" inputmode="url" spellcheck="false" autocapitalize="off"
              placeholder="http://127.0.0.1:8787" value="${esc(currentBridge)}"
              style="width:100%;box-sizing:border-box;margin:8px 0;padding:8px" />
       <button class="btn" id="bridgeSave">Save &amp; reload</button>
       <p class="stamp">blank = use the built-in default (mock if none set)</p>`,
      'wide',
    ) +
    stampLine(s);

  // Wire Save: persist to localStorage.skippyBridge and reload so the store factory
  // re-resolves the client against the new URL. (Reload is the swap — the factory
  // caches the client as a singleton, so a live re-point needs a fresh load.)
  const saveBtn = root.querySelector<HTMLButtonElement>('#bridgeSave');
  const input = root.querySelector<HTMLInputElement>('#bridgeUrl');
  if (saveBtn && input) {
    saveBtn.onclick = () => {
      const v = input.value.trim();
      try {
        if (v) localStorage.setItem('skippyBridge', v);
        else localStorage.removeItem('skippyBridge');
      } catch {
        /* ignore persistence failure — nothing else to do */
      }
      location.reload();
    };
  }
}
